import { Component, Label, Overlay } from "@mcleod/components";
import { Alignment, Api, ArrayUtil, getAuthSettings, Navigation, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "./CommonDialogs";

/**
 * Signs the current user into the Helpjuice knowledge base using JWT-based SSO.
 *
 * The desktop client reaches Helpjuice through HelpjuiceSsoLauncher; //web goes through the
 * lme/general/helpjuice-url endpoint, which returns a short-lived signed url.
 */
export class HelpjuiceLauncher {
    /**
     * Licensing is keyed off the class that drives the classic Knowledge Base menu item, so that a
     * single license entry gates classic, //web, and the endpoint.
     */
    static readonly LICENSE_CLASS_NAME = "com.tms.common.loadmaster.helpjuice.HelpjuiceSsoLauncher";

    /**
     * Tests whether the Knowledge Base should be offered to the current user.  Portal users don't
     * get it, regardless of how the instance is licensed.
     */
    public static isAvailable(): boolean {
        return getAuthSettings()?.user_settings?.is_portal_user !== true
            && HelpjuiceLauncher.isLicensed() === true;
    }

    /**
     * Tests whether Helpjuice is licensed for this user.
     *
     * Licensing arrives with the authenticated settings, so this returns false until the user has
     * logged in.  This version of //web has no LicenseTester, so the licensed and excluded lists are
     * read straight from those settings, applying the same precedence the server uses: an explicitly
     * licensed class wins, otherwise the package must be licensed and the class must not be excluded.
     */
    public static isLicensed(): boolean {
        const license = getAuthSettings()?.license;
        if (license == null)
            return false;
        const className = HelpjuiceLauncher.LICENSE_CLASS_NAME;
        if (license.licensed_classes?.includes(className) === true)
            return true;
        const packageName = className.substring(0, className.lastIndexOf("."));
        if (license.licensed_packages?.includes(packageName) !== true)
            return false;
        return license.excluded_classes?.includes(className) !== true;
    }

    /**
     * Shows the page header's light bulb dropdown.  Knowledge Base is the only entry; it's presented
     * as a dropdown so that the light bulb behaves the way it does in later versions.
     */
    public static showDropdown(anchor: Component) {
        const item = new Label({
            caption: "Knowledge Base",
            paddingLeft: 8, paddingRight: 28, paddingTop: 8, paddingBottom: 8,
            fontSize: "large", color: "subtle.darker",
            onClick: () => HelpjuiceLauncher.open()
        });
        Overlay.showDropdown(anchor, [item], null, { width: null }, { align: Alignment.RIGHT });
    }

    /**
     * Retrieves a freshly signed SSO url and opens it in a new tab.  The token in the url isn't valid
     * for very long, so a new one is requested on every click rather than being cached.
     */
    public static async open(): Promise<void> {
        const response = await Api.post("lme/general/helpjuice-url");
        const data = ArrayUtil.getFirstElement(response?.data);
        const url = data?.url;
        if (StringUtil.isEmptyString(url) !== true)
            Navigation.navigateTo(url, { newTab: true });
        else if (StringUtil.isEmptyString(data?.error_message) !== true)
            CommonDialogs.showDialog(data.error_message);
        else
            CommonDialogs.showDialog("We're sorry, this link is not accessible at this time.");
    }
}
